import React from 'react';
import { 
  AppBar, 
  Toolbar, 
  IconButton, 
  Typography, 
  Drawer, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText,
  Box,
  Divider,
  Button
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CalculateIcon from '@mui/icons-material/Calculate';
import BookIcon from '@mui/icons-material/Book';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import CloseIcon from '@mui/icons-material/Close';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

interface MobileNavigationProps {
  currentView: 'devices' | 'calculations' | 'references';
  onViewChange: (view: 'devices' | 'calculations' | 'references') => void;
  hasResults: boolean;
  onReset: () => void;
}

export const MobileNavigation: React.FC<MobileNavigationProps> = ({
  currentView,
  onViewChange,
  hasResults,
  onReset
}) => {
  const [drawerOpen, setDrawerOpen] = React.useState(false);

  const menuItems = [
    {
      id: 'devices' as const,
      label: 'Pediatric Airways',
      icon: <MedicalServicesIcon />,
      disabled: false // Always allow access to input form and results
    },
    {
      id: 'calculations' as const,
      label: 'ETT Calculations',
      icon: <CalculateIcon />,
      disabled: !hasResults
    },
    {
      id: 'references' as const,
      label: 'References',
      icon: <BookIcon />,
      disabled: false
    }
  ];

  const handleMenuClick = (viewId: 'devices' | 'calculations' | 'references') => {
    if (!menuItems.find(item => item.id === viewId)?.disabled) {
      onViewChange(viewId);
      setDrawerOpen(false);
    }
  };

  const getCurrentViewTitle = () => {
    switch (currentView) {
      case 'devices':
        return '';
      case 'calculations':
        return 'ETT Calculations';
      case 'references':
        return 'References';
      default:
        return 'Peds Airway Calculator';
    }
  };

  return (
    <>
      <AppBar 
        position="sticky" 
        sx={{ 
          backgroundColor: '#1976d2',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}
      >
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => setDrawerOpen(true)}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <Typography 
            variant="h6" 
            component="div" 
            sx={{ 
              flexGrow: 1,
              fontSize: '1.1rem',
              fontWeight: 500
            }}
          >
            {getCurrentViewTitle()}
          </Typography>
          {hasResults && (
            <Button
              color="inherit"
              onClick={onReset}
              startIcon={<RestartAltIcon />}
              sx={{ 
                ml: 1,
                textTransform: 'none',
                fontSize: '0.9rem',
                fontWeight: 500
              }}
              aria-label="Reset calculator"
            >
              Reset
            </Button>
          )}
        </Toolbar>
      </AppBar>

      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: 280,
            backgroundColor: '#f8f9fa'
          }
        }}
      > 
        <List sx={{ pt: 1 }}>
          {menuItems.map((item) => (
            <ListItem
              key={item.id}
              onClick={() => handleMenuClick(item.id)}
              sx={{
                cursor: item.disabled ? 'not-allowed' : 'pointer',
                opacity: item.disabled ? 0.5 : 1,
                backgroundColor: currentView === item.id ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                '&:hover': {
                  backgroundColor: item.disabled ? 'transparent' : 
                    currentView === item.id ? 'rgba(25, 118, 210, 0.12)' : 'rgba(0, 0, 0, 0.04)'
                },
                borderRadius: 1,
                mx: 1,
                mb: 0.5
              }}
            >
              <ListItemIcon sx={{ color: currentView === item.id ? '#1976d2' : 'inherit' }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText 
                primary={item.label}
                sx={{
                  '& .MuiListItemText-primary': {
                    color: currentView === item.id ? '#1976d2' : 'inherit',
                    fontWeight: currentView === item.id ? 600 : 400
                  }
                }}
              />
            </ListItem>
          ))}
        </List>

        {!hasResults && (
          <Box sx={{ p: 2, mt: 'auto', backgroundColor: '#fff3cd', borderRadius: 1, mx: 2, mb: 2 }}>
            <Typography variant="body2" sx={{ color: '#856404', textAlign: 'center' }}>
              Enter patient data in Pediatric Airways to access ETT calculations
            </Typography>
          </Box>
        )}
      </Drawer>
    </>
  );
};